// src/components/forms/LeadForm.tsx — take an enquiry, or work one that came in.
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useListingOptions, usePropertyOptions, useUserOptions } from '../../lib/options';
import { Button, Field, Input, Modal, Select, Textarea } from '../ui';
import { FormError, FormGrid, Wide, nulled, numberOr } from './common';

type L = Record<string, any>;
const STATUSES = ['NEW', 'CONTACTED', 'VIEWING_BOOKED', 'APPLIED', 'CONVERTED', 'LOST'];
const SOURCES = ['WEBSITE', 'AI_CHAT', 'PORTAL', 'PHONE', 'WALK_IN', 'REFERRAL', 'WEBSITE_FORM'];
const label = (s: string) => s.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());

/**
 * Most leads arrive on their own — the website form and the chatbot both write one.
 * This is for the ones that come by phone or in person, and for working the pipeline
 * afterwards. Which fields are editable follows the API: what a lead came in against
 * is history, so only the follow-up fields can change.
 */
export function LeadForm({ open, onClose, lead }: { open: boolean; onClose: () => void; lead?: L }) {
  const isNew = !lead;
  const qc = useQueryClient();
  const properties = usePropertyOptions();
  const listings = useListingOptions(isNew);
  const users = useUserOptions();
  const [form, setForm] = useState(() => ({
    firstName: lead?.firstName ?? '', lastName: lead?.lastName ?? '', email: lead?.email ?? '', phone: lead?.phone ?? '',
    source: lead?.source ?? 'PHONE', status: lead?.status ?? 'NEW', propertyId: lead?.propertyId ?? '', listingId: lead?.listingId ?? '',
    assignedTo: lead?.assignedTo ?? '', notes: lead?.notes ?? '', score: lead?.score == null ? '' : String(lead.score),
    beds: lead?.requirements?.beds == null ? '' : String(lead.requirements.beds),
    budget: lead?.requirements?.budget == null ? '' : String(lead.requirements.budget),
    moveIn: lead?.requirements?.moveIn ?? '',
  }));
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const requirements = () => ({
    ...(form.beds !== '' ? { beds: numberOr(form.beds) } : {}),
    ...(form.budget !== '' ? { budget: numberOr(form.budget) } : {}),
    ...(form.moveIn ? { moveIn: form.moveIn } : {}),
  });

  const save = useMutation({
    mutationFn: async () => (isNew
      ? (await api.post<L>('/leads', {
          firstName: nulled(form.firstName), lastName: nulled(form.lastName), email: nulled(form.email), phone: nulled(form.phone),
          source: form.source, status: form.status, propertyId: nulled(form.propertyId), listingId: nulled(form.listingId),
          assignedTo: nulled(form.assignedTo), notes: nulled(form.notes), requirements: requirements(),
        })).data
      : (await api.patch<L>(`/leads/${lead!.id}`, {
          firstName: nulled(form.firstName), lastName: nulled(form.lastName), email: nulled(form.email), phone: nulled(form.phone),
          status: form.status, assignedTo: nulled(form.assignedTo), notes: nulled(form.notes),
          score: form.score === '' ? null : numberOr(form.score), requirements: requirements(),
        })).data),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['/leads'] }); onClose(); },
  });

  const ready = !!form.email.trim() || !!form.phone.trim();

  return (
    <Modal open={open} onClose={onClose} size="lg" title={isNew ? 'New lead' : 'Lead'}
      footer={<>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={() => save.mutate()} disabled={!ready || save.isPending}>{save.isPending ? 'Saving…' : isNew ? 'Create lead' : 'Save changes'}</Button>
      </>}>
      <div className="space-y-5">
        <FormGrid>
          <Field label="First name"><Input value={form.firstName} onChange={(e) => set({ firstName: e.target.value })} className="w-full" /></Field>
          <Field label="Last name"><Input value={form.lastName} onChange={(e) => set({ lastName: e.target.value })} className="w-full" /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} className="w-full" /></Field>
          <Field label="Phone" hint={ready ? undefined : 'An email address or a phone number is required'}><Input value={form.phone} onChange={(e) => set({ phone: e.target.value })} className="w-full" /></Field>

          {isNew && <>
            <Field label="Source"><Select value={form.source} onChange={(e) => set({ source: e.target.value })} className="w-full">{SOURCES.map((s) => <option key={s} value={s}>{label(s)}</option>)}</Select></Field>
            <Field label="Interested in a scheme"><Select value={form.propertyId} onChange={(e) => set({ propertyId: e.target.value })} className="w-full"><option value="">No preference</option>{(properties.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</Select></Field>
            <Wide><Field label="Interested in a specific home"><Select value={form.listingId} onChange={(e) => set({ listingId: e.target.value })} className="w-full"><option value="">None</option>{(listings.data ?? []).map((l) => <option key={l.id} value={l.id}>{l.headline}</option>)}</Select></Field></Wide>
          </>}

          <Field label="Stage"><Select value={form.status} onChange={(e) => set({ status: e.target.value })} className="w-full">{STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}</Select></Field>
          <Field label="Owner"><Select value={form.assignedTo} onChange={(e) => set({ assignedTo: e.target.value })} className="w-full"><option value="">Unassigned</option>{(users.data ?? []).map((u) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}</Select></Field>

          <Field label="Bedrooms wanted"><Input value={form.beds} onChange={(e) => set({ beds: e.target.value })} inputMode="numeric" placeholder="2" className="w-full" /></Field>
          <Field label="Budget per month"><Input value={form.budget} onChange={(e) => set({ budget: e.target.value })} inputMode="numeric" placeholder="1800" className="w-full" /></Field>
          <Field label="Looking to move"><Input type="date" value={form.moveIn} onChange={(e) => set({ moveIn: e.target.value })} className="w-full" /></Field>
          {!isNew && <Field label="Score" hint="0–100, set by hand or by the model"><Input value={form.score} onChange={(e) => set({ score: e.target.value })} inputMode="numeric" className="w-full" /></Field>}

          <Wide><Field label="Notes"><Textarea rows={3} value={form.notes} onChange={(e) => set({ notes: e.target.value })} className="w-full" /></Field></Wide>
        </FormGrid>
        <FormError error={save.error} />
      </div>
    </Modal>
  );
}
